import { Node, markInputRule } from '@tiptap/core';
import Bold from '@tiptap/extension-bold';
import History from '@tiptap/extension-history';
import { Slice } from '@tiptap/pm/model';
import { EditorContent, useEditor } from '@tiptap/react';
import { useRef } from 'react';
import { Markdown, type MarkdownStorage } from 'tiptap-markdown';

import {
  defaultExtensions,
  Placeholder,
} from '@/components/ui/editor/extensions';
import { cn } from '@/lib/utils';

import {
  fieldErrorClass,
  FIELD_ERROR_PLACEHOLDER_CLASS,
  textAppearanceClass,
} from '../../field/textAppearance';
import { fieldErrorId } from '../../fieldIds';
import { useCardMetrics } from '../../internals/useCardMetrics';
import { MARKDOWN_CLASS, TEXT_BOX_CLASS, TEXT_STYLE } from '../../tokens';

import { mrkdwnDocument, serializeMrkdwn } from './mrkdwnEditor';

import type { EditorProps } from './PlainEditor';

declare module '@tiptap/core' {
  interface Storage {
    markdown: MarkdownStorage;
  }
}

const SlackToken = Node.create({
  name: 'slackToken',
  inline: true,
  group: 'inline',
  atom: true,
  addAttributes: () => ({ source: { default: '' }, label: { default: '' } }),
  parseHTML: () => [{ tag: 'span[data-slack-token]' }],
  renderHTML: ({ node }) => [
    'span',
    { 'data-slack-token': node.attrs.source, class: 'text-kind-act' },
    node.attrs.label,
  ],
});
const SlackBold = Bold.extend({
  addInputRules() {
    return [markInputRule({ find: /(?:^|\s)\*([^*]+)\*$/, type: this.type })];
  },
});

// The font's line boxes reach a pixel past the last line, which would make a fitting
// body scroll, so the content clips its own vertical overflow
export const BODY_CONTENT_CLASS = 'block overflow-y-clip';

export function RichEditor({
  format,
  ...props
}: EditorProps & { format: 'markdown' | 'mrkdwn' }) {
  const metrics = useCardMetrics();
  const latest = useRef(props);
  latest.current = props;
  const extensions = defaultExtensions({ mode: 'readonly' });
  const slackNodes = [
    'doc',
    'text',
    'paragraph',
    'italic',
    'strike',
    'code',
    'codeBlock',
    'blockquote',
    'hardBreak',
    'listItem',
    'bulletList',
    'orderedList',
    'link',
  ];
  const editor = useEditor({
    extensions: [
      History,
      Placeholder.configure({ text: props.placeholder }),
      ...(format === 'mrkdwn'
        ? [
            ...extensions.filter((extension) =>
              slackNodes.includes(extension.name)
            ),
            SlackBold,
            SlackToken,
          ]
        : [
            // The link popover needs the app editor's BaseEditorProvider, and a hover
            // that rendered it outside one threw and blanked the card's body
            ...extensions.filter((extension) => extension.name !== 'linkHover'),
            Markdown.configure({
              html: false,
              transformPastedText: true,
              breaks: true,
            }),
          ]),
    ],
    content: format === 'mrkdwn' ? mrkdwnDocument(props.value) : props.value,
    autofocus: 'end',
    editorProps: {
      attributes: {
        id: props.id,
        role: 'textbox',
        'aria-multiline': 'true',
        ...(props.error
          ? {
              'aria-invalid': 'true',
              'aria-describedby': fieldErrorId(props.id),
            }
          : {}),
        'aria-label': props.ariaLabel ?? 'Message',
        class: cn(
          BODY_CONTENT_CLASS,
          'outline-none min-h-[1lh] whitespace-pre-wrap',
          // In chat the card sits inside markdown, whose paragraph and list margins outrank a plain reset
          format === 'markdown'
            ? 'markdown'
            : '[&_p]:!my-0 [&_ul]:!my-0 [&_ol]:!my-0 [&_li]:!my-0'
        ),
      },
      // Pasted Slack text is read as mrkdwn; the Markdown paste rules would read
      // *bold* as italic and link the URL inside <url|label>
      ...(format === 'mrkdwn'
        ? {
            clipboardTextParser: (text, _context, _plain, view) =>
              Slice.maxOpen(
                view.state.schema.nodeFromJSON(mrkdwnDocument(text)).content
              ),
          }
        : {}),
    },
    enablePasteRules: format !== 'mrkdwn',
    onFocus: () => latest.current.onFocus(),
    onUpdate: ({ editor }) => {
      const next: unknown =
        format === 'mrkdwn'
          ? serializeMrkdwn(editor.getJSON())
          : editor.storage.markdown.getMarkdown();
      if (typeof next === 'string') latest.current.onChange(next);
    },
  });
  return (
    // Closes on the focusout the card also commits on. Closing on the editor's own
    // blur unmounts it before that focusout, so the card would miss the edit
    <div
      onBlur={() => latest.current.onBlur()}
      className={cn(
        TEXT_BOX_CLASS,
        textAppearanceClass(props.appearance, props.outline),
        MARKDOWN_CLASS,
        format === 'markdown' && 'smoothdown',
        TEXT_STYLE[props.textStyle],
        props.bodyClass,
        props.toneClass,
        metrics.bodyMaxHeight,
        'py-0 overflow-y-auto',
        props.error && fieldErrorClass(props.appearance, props.outline),
        props.error && FIELD_ERROR_PLACEHOLDER_CLASS.editor
      )}
    >
      <EditorContent editor={editor} />
    </div>
  );
}
